import { useEffect, useMemo, useRef, useState } from 'react';
import { PencilLine } from 'lucide-react';
import { Tag } from '@/components/Chips';
import { SchemaForm } from '@/components/message/SchemaForm';
import { MessagePicker } from '@/components/try/MessagePicker';
import { StructuredBuilderForm } from '@/components/try/StructuredBuilderForm';
import { StructuredBuilderPanes, type StructuredPaneMode } from '@/components/try/StructuredBuilderPanes';
import type { Selection } from '@/components/try/types';
import { SCHEMA_MODELS } from '@/data/generated/schemaModels';
import type { BuilderInput, StructuredBuilderDef } from '@/lib/editor/types';
import { pairVersionId, versionInputPatch } from '@/lib/editor/versioning';
import { createFieldIndex } from '@/lib/editor/xmlResolve';
import { useT } from '@/i18n';

/** xmlns (+ pair) from catalog version; orgnlMsgNmId only when it names this primary. */
function withVersion(def: StructuredBuilderDef, input: BuilderInput, versionId?: string): BuilderInput {
  if (!versionId) return input;
  const pair = pairVersionId(def.short, versionId);
  const patch = versionInputPatch(versionId, pair);
  const next: BuilderInput = {
    ...input,
    xmlns: patch.xmlns,
    pairXmlns: patch.pairXmlns,
  };
  if (def.short === 'pain.001' && 'orgnlMsgNmId' in next) {
    next.orgnlMsgNmId = patch.orgnlMsgNmId;
  }
  return next;
}

export function StructuredBuilderEditor({
  def,
  versionId,
  embedded = false,
  helpMode = false,
}: {
  def: StructuredBuilderDef;
  versionId?: string;
  embedded?: boolean;
  helpMode?: boolean;
}) {
  const t = useT();
  const formRef = useRef<HTMLElement>(null);
  const { highlightSelectors, resolveXmlClick, fieldByKey } = useMemo(
    () => createFieldIndex(def.fields),
    [def.fields],
  );

  const [input, setInput] = useState<BuilderInput>(() =>
    withVersion(def, { ...def.defaultInput }, versionId),
  );
  const [outcome, setOutcome] = useState(def.outcome.default);
  const [rejectReason, setRejectReason] = useState(def.outcome.rejectOptions?.[0] ?? 'AC01');
  const [editedPrimary, setEditedPrimary] = useState<string | null>(null);
  const [paneMode, setPaneMode] = useState<StructuredPaneMode>('primary');
  const [expert, setExpert] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);

  useEffect(() => {
    if (!versionId) return;
    setEditedPrimary(null);
    setInput((prev) => withVersion(def, prev, versionId));
  }, [def, versionId]);

  const primaryXml = useMemo(() => def.buildPrimary(input), [def, input]);
  const pairXml = useMemo(
    () => def.buildPair(input, outcome, rejectReason),
    [def, input, outcome, rejectReason],
  );
  const displayPrimary = editedPrimary ?? primaryXml;
  const selectedPaths = selection?.paths ?? [];
  const hasSchema = Boolean(SCHEMA_MODELS[def.short]);

  useEffect(() => {
    if (selection?.source !== 'xml') return;
    const el = formRef.current?.querySelector(`[data-field-key="${selection.fieldKey}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    el?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
  }, [selection, expert]);

  function patch(key: string, value: string | boolean) {
    setEditedPrimary(null);
    setInput((prev) => {
      const next = { ...prev, [key]: value };
      if (!expert && def.simpleSync?.[key]) {
        for (const mirror of def.simpleSync[key]!) {
          next[mirror] = value;
        }
      }
      if (key === 'msgId' && !expert && 'statusMsgId' in next) {
        next.statusMsgId = String(value).replace(/PAIN1/i, 'PAIN2');
      }
      return next;
    });
  }

  function activateField(key: string, source: 'form' | 'xml') {
    setSelection((prev) =>
      prev?.fieldKey === key && prev.source === 'xml' && source === 'form'
        ? prev
        : { fieldKey: key, source, paths: highlightSelectors(key) },
    );
  }

  function selectFromXml(selector: string) {
    const hit = resolveXmlClick(selector, expert);
    if (!hit) {
      setSelection(null);
      return;
    }
    if (hit.enableExpert) setExpert(true);
    setSelection({
      fieldKey: hit.field.key,
      source: 'xml',
      paths: [...new Set([selector, ...hit.field.xmlSelectors])],
    });
  }

  function reset() {
    setInput(withVersion(def, { ...def.freshInput() }, versionId));
    setOutcome(def.outcome.default);
    setRejectReason(def.outcome.rejectOptions?.[0] ?? 'AC01');
    setEditedPrimary(null);
    setSelection(null);
  }

  const body = (
    <div
      className={
        hasSchema
          ? 'grid gap-4 xl:grid-cols-[minmax(16rem,22%)_minmax(14rem,20%)_minmax(0,1fr)] xl:items-start'
          : 'grid gap-4 xl:grid-cols-[minmax(18rem,26%)_minmax(0,1fr)] xl:items-start'
      }
    >
      <StructuredBuilderForm
        formRef={formRef}
        def={def}
        input={input}
        outcome={outcome}
        rejectReason={rejectReason}
        expert={expert}
        setExpert={setExpert}
        patch={patch}
        setOutcome={setOutcome}
        setRejectReason={setRejectReason}
        highlighted={selection?.fieldKey ?? null}
        onActivate={activateField}
        onReset={reset}
        fieldByKey={fieldByKey}
        t={t}
      />
      {hasSchema && (
        <SchemaForm
          short={def.short}
          xml={displayPrimary}
          onXmlChange={setEditedPrimary}
          helpMode={helpMode}
          selectedPaths={selectedPaths}
          onSelectPath={selectFromXml}
        />
      )}
      <StructuredBuilderPanes
        primaryShort={def.short}
        pairShort={def.pairShort}
        primaryContent={displayPrimary}
        pairContent={pairXml}
        paneMode={paneMode}
        setPaneMode={setPaneMode}
        selectedPaths={selectedPaths}
        onSelectPath={selectFromXml}
        onEditPrimary={setEditedPrimary}
        t={t}
      />
    </div>
  );

  if (embedded) {
    return <div className="flex flex-col gap-4">{body}</div>;
  }

  return (
    <div className="page-fluid flex flex-col gap-4">
      <header className="shrink-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-4xl">
            <p className="eyebrow inline-flex items-center gap-1.5">
              <PencilLine size={12} aria-hidden />
              {t('try.eyebrow')}
            </p>
            <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
              {t('try.titleStructured', { primary: def.short, pair: def.pairShort })}
            </h1>
            <p className="mt-1.5 text-[14px] leading-relaxed text-muted">
              {t(`try.${def.short.split('.')[0]}.lead`)}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Tag>{def.short}</Tag>
            <Tag>{def.pairShort}</Tag>
            <Tag>XML</Tag>
            <Tag>{t('try.tagBuilder')}</Tag>
          </div>
        </div>
      </header>

      <div className="max-w-xs">
        <MessagePicker short={def.short} />
      </div>

      {body}
    </div>
  );
}
